/**
 * US sales-tax data for restaurant meals.
 *
 * Restaurant food is taxed at the **combined rate where the restaurant is**:
 * the state portion plus county, city and special-district taxes (and, in some
 * states, a dedicated meals tax). A single "state rate" is therefore never the
 * rate a guest pays — New York is 4% state but 8.875% in New York City — so the
 * dashboard composes the rate from a state portion and a local portion, and
 * this file supplies the state portion plus the highest local surtax observed
 * in the state as guidance, never as a promise.
 *
 * `baseBps` is the state portion that applies to restaurant meals (special
 * statewide meals rates are used where they differ: CT 7.35%, DC 10%).
 * `maxLocalBps` is the highest local surtax observed in the state (0 when the
 * state has no local sales tax). `examples` are well-established combined
 * rates for major cities, offered as one-click fills.
 *
 * Source: Wikipedia "Sales taxes in the United States" (base + maximum local
 * surtax columns) and well-known city rates, retrieved September 2026. Rates
 * change and local jurisdictions are not fully covered by any single number —
 * the UI always keeps an exact custom input and a lookup link.
 */
export interface UsTaxExample {
  /** e.g. "New York City" or "Most Texas cities". */
  label: string;
  /** Combined state + local rate for restaurant food, in bps. */
  rateBps: number;
}

export interface UsStateTax {
  /** Two-letter USPS code. */
  code: string;
  name: string;
  /** State portion that applies to restaurant meals, in basis points. */
  baseBps: number;
  /** Highest local surtax observed in the state, in bps. 0 = no local sales tax. */
  maxLocalBps: number;
  /** Short, factual guidance (special meals rates, no-sales-tax states). */
  note?: string;
  /** Well-established combined rates owners can click. */
  examples?: UsTaxExample[];
}

export const US_STATE_TAXES: readonly UsStateTax[] = [
  { code: 'AL', name: 'Alabama', baseBps: 400, maxLocalBps: 950 },
  {
    code: 'AK',
    name: 'Alaska',
    baseBps: 0,
    maxLocalBps: 700,
    note: 'No state sales tax; boroughs and cities may tax meals.',
  },
  { code: 'AZ', name: 'Arizona', baseBps: 560, maxLocalBps: 513 },
  { code: 'AR', name: 'Arkansas', baseBps: 650, maxLocalBps: 513 },
  {
    code: 'CA',
    name: 'California',
    baseBps: 725,
    maxLocalBps: 400,
    note: 'District taxes vary by city; Los Angeles is 9.5% and some LA County cities are 10.25%.',
    examples: [
      { label: 'Los Angeles', rateBps: 950 },
      { label: 'San Francisco', rateBps: 862.5 },
    ],
  },
  {
    code: 'CO',
    name: 'Colorado',
    baseBps: 290,
    maxLocalBps: 710,
    note: 'Home-rule cities (Denver, Boulder) set their own higher rates.',
  },
  {
    code: 'CT',
    name: 'Connecticut',
    baseBps: 735,
    maxLocalBps: 0,
    note: 'Restaurant meals are taxed at 7.35%; there is no local sales tax.',
  },
  { code: 'DE', name: 'Delaware', baseBps: 0, maxLocalBps: 0, note: 'No state sales tax.' },
  {
    code: 'DC',
    name: 'District of Columbia',
    baseBps: 1000,
    maxLocalBps: 0,
    note: 'Restaurant meals are taxed at 10%; there is no local sales tax.',
  },
  {
    code: 'FL',
    name: 'Florida',
    baseBps: 600,
    maxLocalBps: 150,
    note: 'County surtaxes apply to restaurant food (Miami-Dade is 7%).',
    examples: [
      { label: 'Miami-Dade', rateBps: 700 },
      { label: 'Orlando', rateBps: 650 },
    ],
  },
  { code: 'GA', name: 'Georgia', baseBps: 400, maxLocalBps: 400 },
  {
    code: 'HI',
    name: 'Hawaii',
    baseBps: 417,
    maxLocalBps: 55,
    note: 'General excise tax; Oahu adds a 0.5% county surcharge.',
  },
  { code: 'ID', name: 'Idaho', baseBps: 600, maxLocalBps: 250 },
  {
    code: 'IL',
    name: 'Illinois',
    baseBps: 625,
    maxLocalBps: 400,
    note: 'Chicago adds its own taxes (10.25% general rate).',
  },
  { code: 'IN', name: 'Indiana', baseBps: 700, maxLocalBps: 0, note: 'No local sales tax.' },
  { code: 'IA', name: 'Iowa', baseBps: 600, maxLocalBps: 100 },
  { code: 'KS', name: 'Kansas', baseBps: 650, maxLocalBps: 550 },
  { code: 'KY', name: 'Kentucky', baseBps: 600, maxLocalBps: 0, note: 'No local sales tax.' },
  { code: 'LA', name: 'Louisiana', baseBps: 500, maxLocalBps: 850 },
  { code: 'ME', name: 'Maine', baseBps: 550, maxLocalBps: 0, note: 'No local sales tax.' },
  { code: 'MD', name: 'Maryland', baseBps: 600, maxLocalBps: 0, note: 'No local sales tax.' },
  {
    code: 'MA',
    name: 'Massachusetts',
    baseBps: 625,
    maxLocalBps: 75,
    note: 'Localities may add a 0.75% meals tax (Boston is 7%).',
    examples: [{ label: 'Boston', rateBps: 700 }],
  },
  { code: 'MI', name: 'Michigan', baseBps: 600, maxLocalBps: 0, note: 'No local sales tax.' },
  { code: 'MN', name: 'Minnesota', baseBps: 687.5, maxLocalBps: 100 },
  { code: 'MS', name: 'Mississippi', baseBps: 700, maxLocalBps: 25 },
  {
    code: 'MO',
    name: 'Missouri',
    baseBps: 422.5,
    maxLocalBps: 662,
    note: 'Kansas City and St. Louis add local meals taxes.',
  },
  { code: 'MT', name: 'Montana', baseBps: 0, maxLocalBps: 0, note: 'No state sales tax.' },
  { code: 'NE', name: 'Nebraska', baseBps: 550, maxLocalBps: 200 },
  {
    code: 'NV',
    name: 'Nevada',
    baseBps: 685,
    maxLocalBps: 153,
    note: 'Clark County (Las Vegas) is 8.375%.',
    examples: [{ label: 'Las Vegas', rateBps: 837.5 }],
  },
  { code: 'NH', name: 'New Hampshire', baseBps: 0, maxLocalBps: 0, note: 'No state sales tax.' },
  {
    code: 'NJ',
    name: 'New Jersey',
    baseBps: 662.5,
    maxLocalBps: 600,
    note: 'Statewide rate with no local sales tax in most of the state.',
  },
  {
    code: 'NM',
    name: 'New Mexico',
    baseBps: 512.5,
    maxLocalBps: 356,
    note: 'Gross receipts tax; local rates vary.',
  },
  {
    code: 'NY',
    name: 'New York',
    baseBps: 400,
    maxLocalBps: 487.5,
    note: 'New York City is 8.875% (4% state + 4.5% city + 0.375% MCTD).',
    examples: [{ label: 'New York City', rateBps: 887.5 }],
  },
  { code: 'NC', name: 'North Carolina', baseBps: 475, maxLocalBps: 350, note: 'Some localities add a meals tax.' },
  { code: 'ND', name: 'North Dakota', baseBps: 500, maxLocalBps: 300 },
  { code: 'OH', name: 'Ohio', baseBps: 575, maxLocalBps: 225 },
  { code: 'OK', name: 'Oklahoma', baseBps: 450, maxLocalBps: 650 },
  { code: 'OR', name: 'Oregon', baseBps: 0, maxLocalBps: 0, note: 'No state sales tax.' },
  {
    code: 'PA',
    name: 'Pennsylvania',
    baseBps: 600,
    maxLocalBps: 200,
    note: 'Philadelphia is 8% (2% city); most of the state is 6%.',
    examples: [{ label: 'Philadelphia', rateBps: 800 }],
  },
  { code: 'RI', name: 'Rhode Island', baseBps: 700, maxLocalBps: 0, note: 'No local sales tax.' },
  {
    code: 'SC',
    name: 'South Carolina',
    baseBps: 600,
    maxLocalBps: 300,
    note: 'Local hospitality taxes can add up to 2% on prepared food.',
  },
  { code: 'SD', name: 'South Dakota', baseBps: 420, maxLocalBps: 200 },
  { code: 'TN', name: 'Tennessee', baseBps: 700, maxLocalBps: 275 },
  {
    code: 'TX',
    name: 'Texas',
    baseBps: 625,
    maxLocalBps: 200,
    note: 'Local rate is capped at 2% (most Texas cities are 8.25%).',
    examples: [{ label: 'Most Texas cities', rateBps: 825 }],
  },
  { code: 'UT', name: 'Utah', baseBps: 610, maxLocalBps: 225 },
  { code: 'VT', name: 'Vermont', baseBps: 600, maxLocalBps: 100 },
  {
    code: 'VA',
    name: 'Virginia',
    baseBps: 530,
    maxLocalBps: 170,
    note: 'Many localities add a meals tax on prepared food.',
  },
  { code: 'WA', name: 'Washington', baseBps: 650, maxLocalBps: 410 },
  { code: 'WV', name: 'West Virginia', baseBps: 600, maxLocalBps: 100 },
  {
    code: 'WI',
    name: 'Wisconsin',
    baseBps: 500,
    maxLocalBps: 290,
    note: 'Some cities add a food and beverage tax.',
  },
  { code: 'WY', name: 'Wyoming', baseBps: 400, maxLocalBps: 200 },
] as const;

/** Lookup by USPS code; null when the state is not in the list. */
export function stateTax(code: string | null | undefined): UsStateTax | null {
  if (!code) return null;
  const normalized = code.trim().toUpperCase();
  return US_STATE_TAXES.find((state) => state.code === normalized) ?? null;
}

/**
 * A state whose **state portion** exactly matches a stored rate. Used only to
 * preselect the breakdown when the saved value is unambiguous; anything else is
 * treated as a custom combined rate.
 */
export function stateForBaseRate(rateBps: number | null | undefined): UsStateTax | null {
  if (rateBps == null) return null;
  return US_STATE_TAXES.find((state) => Math.round(state.baseBps * 10) === Math.round(rateBps * 10)) ?? null;
}
